import moduleStyles from "components/module/styles.module.css";

import React, { useState, useEffect, use }  from 'react';
import { AppointmentProps, ShiftPlanPeriodProps } from './props';
import Repository from './api/Repository';
import { ModuleOnline } from "components/module/ModuleOnline";
import { ModuleWithData } from "components/module/ModuleWithData";
import { IconPicker } from "components/layout/icons/IconPicker";
import Spacer from "components/layout/Spacer";
import { ColorService } from "components/services/ColorService";
import { useStoreSelector } from "components/hooks/useStoreSelector";
import { AppStore } from "components/stores/AppStore";
import moment from 'moment';
import { useNavigate } from "react-router";
import { Module } from "./Module";

interface PromiseProps {
    shiftPlanPeriods: ShiftPlanPeriodProps[],
    appointments: AppointmentProps[]
}

const DefaultPromiseProps: PromiseProps = {
    shiftPlanPeriods: [],
    appointments: [],
}

interface DashboardProps {
    refresh: boolean
}

export const Dashboard = ({ refresh }: DashboardProps) => {

    const navigate = useNavigate();
    const [dataPromise, setDataPromise] = useState<null | Promise<PromiseProps>>(null);

    const fetchOnline = async () => {
        const response = await Repository.getCalendarContentTodayAsync();

        const result = await response.json();
        return result as PromiseProps;
    }

    const fetchData = (): Promise<PromiseProps> => {
        if (navigator.onLine) {
            return fetchOnline();
        } else {
            return new Promise<PromiseProps>((resolve) => resolve(DefaultPromiseProps));
        }
    }

    const onClick = () => {
        navigate(Module.url);
    }

    useEffect(() => {
        setDataPromise(fetchData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!refresh) return;
        setDataPromise(fetchData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh]);
    
    return (
        <ModuleWithData
            module={Module}
            dataPromise={dataPromise}
            errorText="Kunne ikke hente kalender data.">
            <ModuleCalendarContent
                dataPromise={dataPromise}
                onClick={onClick}
                />
        </ModuleWithData>
    )
}

interface ModuleCalendarContentProps { 
    dataPromise: Promise<PromiseProps>;
    onClick: () => void;
}
export const ModuleCalendarContent = ({ dataPromise, onClick }: ModuleCalendarContentProps) => {
    const data = use<PromiseProps>(dataPromise);
    const [background, setBackground] = useState("var(--home-default-bg)");

    const darkmode = useStoreSelector(AppStore, (state) => state.darkmode);
    const shiftPlanPeriod = (data.shiftPlanPeriods.length > 0) ? data.shiftPlanPeriods[0] : null;

    const updateBackground = () => {
        if (!shiftPlanPeriod) return;
        const color = shiftPlanPeriod.color;
        const opacity = darkmode ? 0.6 : 0.3;
        setBackground(ColorService.opacityHexColor(color, opacity))
    }

    const getShiftPlanPeriodDescription = () => {
        if (!shiftPlanPeriod) return null;
        return (
            <>
                <div>
                    {shiftPlanPeriod.typeName}: {moment(shiftPlanPeriod.start).format("DD.MM HH:mm")} - {moment(shiftPlanPeriod.end).format("DD.MM HH:mm")}
                </div>
                <Spacer height={5} />
            </>
        );
    }

    const getAppointmentsDescription = () => {
        if (data.appointments.length === 0) return null;
        return (
            <>
                <div>
                    Du har {data.appointments.length} {data.appointments.length === 1 ? "avtale" : "avtaler"} i dag.
                </div>
                <Spacer height={5} />
            </>
        )
    }

    useEffect(() => {
        updateBackground();
    }, [darkmode]);

    return (
        data &&
        <ModuleOnline module={Module}>
            <div
                onClick={onClick}
                    className={moduleStyles.bar}
                style={{ background: background }}>
                    <div className={moduleStyles.barContent}>
                    <div>
                        <div role="heading">Kalender</div>
                        <div role="note">
                            <Spacer height={5} />
                            {getShiftPlanPeriodDescription()}
                            {getAppointmentsDescription() }
                        </div>
                    </div>
                    <div>
                        <IconPicker icon="calendar-dots" size={24} />
                    </div>
                </div>
            </div>
        </ModuleOnline>
    )
}
